/**
 * MainLayout Component
 * Single column layout with scrollable invoice preview on mobile
 * Hides form when invoice is generated, shows actions instead
 */

import { InvoiceForm } from '@/components/invoice/InvoiceForm';
import { InvoicePreview } from '@/components/invoice/InvoicePreview';
import { InvoiceActions } from '@/components/invoice/InvoiceActions';
import { RateLimitModal } from '@/components/rate-limit/RateLimitModal';
import { AdModal } from '@/components/common/AdModal';
import { useInvoiceGeneration } from '@/hooks/useInvoiceGeneration';
import { ThemeSwitcher } from '@/components/theme/ThemeSwitcher';
import { LanguageSwitcher } from '@/components/language/LanguageSwitcher';
import { HistoryMenu } from '@/components/history/HistoryMenu';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'next-themes';
import { useInvoiceStore } from '@/stores/invoiceStore';
import { useState } from 'react';

export function MainLayout() {
  const {
    generateInvoice,
    isGenerating,
    showRateLimitModal,
    rateLimitData,
    handleBonusAdded,
    handleCloseModal,
  } = useInvoiceGeneration();
  const { t } = useTranslation();
  const { resolvedTheme } = useTheme();
  const { currentInvoice } = useInvoiceStore();
  const [showAd, setShowAd] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header role="banner" className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="w-full px-3 sm:px-4 lg:px-8 py-2">
          <div className="flex items-center justify-between gap-2 sm:gap-3">
            <button
              onClick={() => useInvoiceStore.getState().reset()}
              className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity cursor-pointer text-left"
              aria-label="Reset and create new invoice"
            >
              <img
                src={resolvedTheme === 'dark' ? '/favicon.dark.png' : '/favicon.png'}
                alt="Smart Invoice Generator Logo"
                className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 flex-shrink-0"
              />
              <div className="min-w-0 text-left">
                <h1 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 dark:text-gray-100 truncate hidden sm:block text-left">
                  {t('app.title')}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:block text-left">
                  {t('app.subtitle')}
                </p>
              </div>
            </button>
            <nav aria-label="Main navigation" className="flex items-center gap-1.5 sm:gap-2 lg:gap-3 flex-shrink-0">
              {/* Desktop Navigation Links */}
              <div className="hidden lg:flex items-center gap-4 mr-2">
                <a href="/about" className="text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  About
                </a>
                <a href="/faq" className="text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  FAQ
                </a>
                <a href="/contact" className="text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Contact
                </a>
              </div>

              <a
                href="https://ko-fi.com/H2H51T8YXB"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center"
                aria-label="Support us on Ko-fi"
              >
                <img
                  height="36"
                  style={{ border: '0px', height: '36px' }}
                  src="https://storage.ko-fi.com/cdn/kofi6.png?v=6"
                  alt="Buy Me a Coffee at ko-fi.com"
                />
              </a>
              <HistoryMenu />
              <ThemeSwitcher />
              <LanguageSwitcher />
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content - Full Width Two Column Layout on Desktop */}
      <main
        id="main-content"
        role="main"
        className="w-full px-4 sm:px-6 lg:px-8 py-6"
        aria-label="Invoice generator and preview"
      >
        {/* Grid: Stack on mobile, side-by-side on lg+ screens (40% form, 60% preview) */}
        <div className="lg:grid lg:grid-cols-[2fr_3fr] lg:gap-8 lg:items-start">
          {/* Left Column - Form OR Actions */}
          <section
            aria-label={currentInvoice ? "Invoice actions" : "Create invoice"}
            className="lg:sticky lg:top-6 lg:self-start"
          >
            {currentInvoice ? (
              <InvoiceActions onNewInvoice={() => useInvoiceStore.getState().reset()} />
            ) : (
              <InvoiceForm
                onGenerate={generateInvoice}
                isGenerating={isGenerating}
              />
            )}
          </section>

          {/* Right Column - Preview */}
          <section
            aria-label="Invoice preview"
            className="mt-6 lg:mt-0 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pr-2"
          >
            <InvoicePreview showActions={false} onExportComplete={() => setShowAd(true)} />
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer role="contentinfo" className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-8">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-6xl mx-auto">
            {/* Footer Links Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
              {/* Product */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Product</h4>
                <ul className="space-y-2">
                  <li>
                    <a href="/" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      Invoice Generator
                    </a>
                  </li>
                  <li>
                    <a href="/history" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      Invoice History
                    </a>
                  </li>
                </ul>
              </div>

              {/* Resources */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Resources</h4>
                <ul className="space-y-2">
                  <li>
                    <a href="/faq" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      FAQ & Help
                    </a>
                  </li>
                  <li>
                    <a href="/guide" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      Invoice Guide
                    </a>
                  </li>
                </ul>
              </div>

              {/* Company */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Company</h4>
                <ul className="space-y-2">
                  <li>
                    <a href="/about" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      About Us
                    </a>
                  </li>
                  <li>
                    <a href="/contact" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      Contact
                    </a>
                  </li>
                </ul>
              </div>

              {/* Legal */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Legal</h4>
                <ul className="space-y-2">
                  <li>
                    <a href="/privacy" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      Privacy Policy
                    </a>
                  </li>
                  <li>
                    <a href="/terms" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      Terms of Service
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Bottom Footer */}
            <div className="pt-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center sm:text-left">
                {t('app.footer')}
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="https://ko-fi.com/H2H51T8YXB"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                  aria-label="Support on Ko-fi"
                >
                  Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Rate Limit Modal - Rendered at top level to ensure proper z-index */}
      {showRateLimitModal && rateLimitData && (
        <RateLimitModal
          isOpen={showRateLimitModal}
          onClose={handleCloseModal}
          limitData={rateLimitData}
          sessionId={null} // Will be handled internally by the hook
          onBonusAdded={handleBonusAdded}
        />
      )}

      {/* Post-download ad modal - Rendered at top level to ensure proper z-index */}
      <AdModal
        isOpen={showAd}
        onClose={() => setShowAd(false)}
        title={t('export.downloadedTitle')}
        message={t('export.downloadedMessage')}
        primaryButtonText={t('export.createAnother')}
        secondaryButtonText={t('export.close')}
      />
    </div>
  );
}
